import { useAuth } from '@/stores/auth'
import { currentCompanyId } from '@/lib/companyUrl'

export class ApiError extends Error {
  status: number
  detail: string
  constructor(status: number, detail: string) {
    super(detail)
    this.status = status
    this.detail = detail
  }
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown
  noAuth?: boolean
}

// 상태코드 → 유저용 안내문(개발자용 코드 대신). 서버가 준 한국어 detail 이 있으면 그걸 우선.
const STATUS_MESSAGE: Record<number, string> = {
  400: '입력한 내용을 다시 확인해 주세요.',
  403: '이 작업을 할 권한이 없어요.',
  404: '찾을 수 없어요.',
  409: '이미 등록되어 있어요.',
  413: '파일이 너무 커요.',
  429: '요청이 많아요. 잠시 후 다시 시도해 주세요.',
  // 500 은 서버에서 처리 못 한 오류(대개 재시도해도 계속 실패) — '일시적' 이라고 안내하지 않는다.
  500: '서버에서 오류가 발생했어요. 문제가 계속되면 관리자에게 문의해 주세요.',
  // 502/504 는 게이트웨이·타임아웃 — 진짜 일시적이라 재시도 안내가 맞다.
  502: '일시적인 오류예요. 잠시 후 다시 시도해 주세요.',
  503: '서버 점검 중이거나 일시적인 오류예요. 잠시 후 다시 시도해 주세요.',
  504: '응답이 지연되고 있어요. 잠시 후 다시 시도해 주세요.',
}
function friendlyStatus(status: number): string {
  return STATUS_MESSAGE[status] ?? '요청을 처리하지 못했어요. 잠시 후 다시 시도해 주세요.'
}
// DRF 에러 본문에서 사람이 읽을 메시지 추출: {detail} 또는 {필드:["메시지"]} / non_field_errors.
function extractDetail(j: unknown): string | null {
  if (!j || typeof j !== 'object') return null
  const o = j as Record<string, unknown>
  if (typeof o.detail === 'string') return o.detail
  for (const key of Object.keys(o)) {
    const v = o[key]
    if (Array.isArray(v) && v.length && typeof v[0] === 'string') return v[0] as string
    if (typeof v === 'string') return v
  }
  return null
}

export async function api<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { body, noAuth, headers: extraHeaders, ...rest } = opts
  const headers = new Headers(extraHeaders)

  const { token } = useAuth.getState()
  if (!noAuth && token) headers.set('Authorization', `Bearer ${token}`)
  // 회사는 현재 창 URL(?company=id)에서 — 창마다 독립.
  const companyId = currentCompanyId()
  if (companyId) headers.set('X-Company-Id', String(companyId))

  let finalBody: BodyInit | undefined
  if (body instanceof FormData) {
    finalBody = body
  } else if (body !== undefined) {
    headers.set('Content-Type', 'application/json')
    finalBody = JSON.stringify(body)
  }

  let r: Response
  try {
    r = await fetch(path, { ...rest, headers, body: finalBody })
  } catch {
    throw new ApiError(0, '인터넷 연결을 확인해 주세요.')
  }

  if (r.status === 401) {
    useAuth.getState().logout()
    // 라우터가 인증 가드로 알아서 /login으로 보냄
    throw new ApiError(401, '로그인이 만료됐어요. 다시 로그인해 주세요.')
  }
  if (!r.ok) {
    let detail = ''
    try { detail = extractDetail(await r.json()) ?? '' } catch {}
    // 서버가 준 안내문이 있으면 그대로, 없으면(HTTP 500/400 등) 유저용 문구로.
    throw new ApiError(r.status, detail || friendlyStatus(r.status))
  }
  if (r.status === 204) return null as T
  const ct = r.headers.get('content-type') || ''
  if (ct.includes('application/json')) return (await r.json()) as T
  return (await r.text()) as unknown as T
}
